/**
 * Support tickets: submit (with similar-ticket check) + owner review board.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

export const ISSUE_TYPES = [
  { value: 'cant_complete', label: "Can't complete step / command rejected" },
  { value: 'progress_lost', label: 'Progress reset / lost' },
  { value: 'quiz_next', label: 'Quiz / Next button' },
  { value: 'login_license', label: 'Login / license' },
  { value: 'learning_center', label: 'Learning Center / slides' },
  { value: 'bug_display', label: 'Bug / display' },
  { value: 'recommendation', label: 'Recommendation / idea' },
  { value: 'other', label: 'Other' }
];

/** Matches server SUPPORT_SCREENSHOT_MAX_BYTES in index-local.js */
export const SCREENSHOT_MAX_BYTES = 4 * 1024 * 1024;
export const SCREENSHOT_MAX_LABEL = '4 MB';
export const IMAGE_EXCEEDS_MAX_MSG = `Image exceeds ${SCREENSHOT_MAX_LABEL} max size`;

/** Friendlier copy when the session cookie is missing or expired (CL-16). */
export function formatSupportApiError(err, fallback = 'Request failed') {
  const raw = String(err == null ? '' : err).replace(/_/g, ' ').trim();
  const lower = raw.toLowerCase();
  if (
    lower === 'no authentication token' ||
    lower === 'invalid or expired token' ||
    lower.includes('authentication token') ||
    lower.includes('expired token')
  ) {
    return 'Your login session expired. Refresh the page and sign in with Google again, then retry.';
  }
  if (lower === 'screenshot too large' || lower.includes('screenshot too large')) {
    return IMAGE_EXCEEDS_MAX_MSG;
  }
  return raw || fallback;
}

const LINUX_LEVELS = [
  { n: 0, title: 'Terminal Tutorial' },
  { n: 1, title: 'Navigation' },
  { n: 2, title: 'File Management' },
  { n: 3, title: 'Text Editing' },
  { n: 4, title: 'Search & Find' },
  { n: 5, title: 'Permissions' },
  { n: 6, title: 'Processes' },
  { n: 7, title: 'Users & Groups' },
  { n: 8, title: 'Compression' },
  { n: 9, title: 'Phase 1 Review' },
  { n: 10, title: 'Phase 1 Assessment' }
];

function issueLabel(v) {
  return ISSUE_TYPES.find((t) => t.value === v)?.label || v || '—';
}

function emailPrefix(email) {
  const s = String(email || '');
  const i = s.indexOf('@');
  return i > 0 ? s.slice(0, i) : s || 'unknown';
}

function formatTicketWhen(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  } catch {
    return String(iso);
  }
}

const fieldSx = {
  '& .MuiOutlinedInput-root': { backgroundColor: 'rgba(0,0,0,0.25)' },
  '& .MuiInputLabel-root': { color: 'rgba(200, 230, 255, 0.85)' },
  '& .MuiOutlinedInput-input': { color: '#e8f4ff' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(120, 200, 255, 0.35)' }
};

export default function SupportTicketPanel({ open, onClose, mode = 'submit', initialLevel = '' }) {
  const isAdmin = mode === 'admin';
  const [levelNumber, setLevelNumber] = useState(initialLevel === '' || initialLevel == null ? '' : String(initialLevel));
  const [exerciseNumber, setExerciseNumber] = useState('');
  const [issueType, setIssueType] = useState('');
  const [body, setBody] = useState('');
  const [screenshotDataUrl, setScreenshotDataUrl] = useState('');
  const [screenshotName, setScreenshotName] = useState('');
  const [similar, setSimilar] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [adminItems, setAdminItems] = useState([]);
  const [adminFilter, setAdminFilter] = useState('open');
  const [adminLoading, setAdminLoading] = useState(false);

  const resetSubmitForm = useCallback(() => {
    setExerciseNumber('');
    setIssueType('');
    setBody('');
    setScreenshotDataUrl('');
    setScreenshotName('');
    setSimilar([]);
    setMessage('');
    setError('');
  }, []);

  useEffect(() => {
    if (!open) return;
    setError('');
    setMessage('');
    if (!isAdmin) {
      setLevelNumber(initialLevel === '' || initialLevel == null ? '' : String(initialLevel));
    }
  }, [open, isAdmin, initialLevel]);

  const loadSimilar = useCallback(async () => {
    if (!issueType) {
      setSimilar([]);
      return;
    }
    setLoadingSimilar(true);
    try {
      const params = new URLSearchParams({ issueType });
      if (levelNumber !== '') params.set('levelNumber', levelNumber);
      if (exerciseNumber !== '') params.set('exerciseNumber', exerciseNumber);
      const r = await fetch(`/api/support/tickets/similar?${params}`, {
        credentials: 'include',
        headers: { Accept: 'application/json' }
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setSimilar([]);
        return;
      }
      setSimilar(Array.isArray(data.items) ? data.items : []);
    } catch {
      setSimilar([]);
    } finally {
      setLoadingSimilar(false);
    }
  }, [issueType, levelNumber, exerciseNumber]);

  useEffect(() => {
    if (!open || isAdmin) return;
    const t = setTimeout(loadSimilar, 200);
    return () => clearTimeout(t);
  }, [open, isAdmin, loadSimilar]);

  const loadAdmin = useCallback(async () => {
    setAdminLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (adminFilter === 'recommendation') params.set('issueType', 'recommendation');
      else if (adminFilter === 'resolved') params.set('status', 'resolved');
      else if (adminFilter === 'open') params.set('status', 'open');
      const r = await fetch(`/api/admin/support-tickets?${params}`, {
        credentials: 'include',
        headers: { Accept: 'application/json' }
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(data.error ? String(data.error).replace(/_/g, ' ') : 'Failed to load tickets');
        setAdminItems([]);
        return;
      }
      setAdminItems(Array.isArray(data.items) ? data.items : []);
    } catch {
      setError('Network error loading tickets');
      setAdminItems([]);
    } finally {
      setAdminLoading(false);
    }
  }, [adminFilter]);

  useEffect(() => {
    if (open && isAdmin) loadAdmin();
  }, [open, isAdmin, loadAdmin]);

  useEffect(() => {
    if (!open || isAdmin) return undefined;
    const t = window.setTimeout(() => {
      if (typeof window.__skyliteRequestRetranslate === 'function') {
        window.__skyliteRequestRetranslate({ force: true });
      }
    }, 100);
    return () => window.clearTimeout(t);
  }, [open, isAdmin, similar, message]);

  const onPickFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!/^image\/(png|jpeg|jpg|webp)$/i.test(file.type)) {
      setError('Screenshot must be PNG, JPEG, or WebP.');
      return;
    }
    if (file.size > SCREENSHOT_MAX_BYTES) {
      setError(IMAGE_EXCEEDS_MAX_MSG);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setScreenshotDataUrl(String(reader.result || ''));
      setScreenshotName(file.name || 'screenshot.png');
      setError('');
    };
    reader.readAsDataURL(file);
  };

  const meToo = async (ticketId) => {
    setError('');
    try {
      const r = await fetch(`/api/support/tickets/${ticketId}/me-too`, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' }
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(formatSupportApiError(data.error, 'Could not join ticket'));
        return;
      }
      setMessage(`Counted on ${data.publicId || 'ticket'} (${data.meTooCount || 0} reports).`);
      loadSimilar();
    } catch {
      setError('Network error');
    }
  };

  const submitNew = async () => {
    setError('');
    setMessage('');
    if (!issueType) {
      setError('Select an issue type.');
      return;
    }
    if (!String(body || '').trim()) {
      setError('Describe the issue.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch('/api/support/tickets', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          levelNumber: levelNumber === '' ? null : Number(levelNumber),
          exerciseNumber: exerciseNumber === '' ? null : Number(exerciseNumber),
          issueType,
          body: body.trim(),
          screenshotDataUrl: screenshotDataUrl || null,
          screenshotName: screenshotName || null
        })
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(formatSupportApiError(data.error, 'Submit failed'));
        return;
      }
      setMessage(`Submitted ${data.publicId}. Thank you.`);
      resetSubmitForm();
    } catch {
      setError(screenshotDataUrl ? IMAGE_EXCEEDS_MAX_MSG : 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const setStatus = async (ticketId, status) => {
    try {
      const r = await fetch(`/api/admin/support-tickets/${ticketId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ status })
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(data.error ? String(data.error).replace(/_/g, ' ') : 'Update failed');
        return;
      }
      if (status === 'resolved') {
        const n = Number(data.teachersNotified) || 0;
        const emails = Array.isArray(data.notifiedTeacherEmails) ? data.notifiedTeacherEmails.filter(Boolean) : [];
        setMessage(
          n > 0
            ? `Marked complete — teacher notified: ${emails.join(', ') || `${n} teacher(s)`}.`
            : 'Marked complete — no class teacher found for this student (no dashboard notice sent).'
        );
      } else {
        setMessage('');
      }
      loadAdmin();
    } catch {
      setError('Network error');
    }
  };

  const levelOptions = useMemo(() => {
    const opts = [{ value: '', label: 'Any / not sure' }];
    // Match learning-center Report Issue + pathway max (server allows 0–200)
    for (let n = 0; n <= 143; n += 1) {
      const known = LINUX_LEVELS.find((l) => l.n === n);
      opts.push({ value: String(n), label: known ? `Level ${n}: ${known.title}` : `Level ${n}` });
    }
    return opts;
  }, []);

  if (!open) return null;

  return (
    <Box
      className="notranslate"
      translate="no"
      dir="ltr"
      lang="en"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 32000,
        bgcolor: 'rgba(2, 8, 20, 0.72)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
      onClick={onClose}
    >
      <Paper
        elevation={0}
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: 'min(720px, 100%)',
          maxHeight: '92dvh',
          overflow: 'auto',
          p: 2.5,
          borderRadius: 2,
          border: '1px solid rgba(120, 200, 255, 0.35)',
          background: 'linear-gradient(165deg, rgba(16, 28, 52, 0.98), rgba(6, 10, 22, 0.99))',
          color: '#e8f4ff'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {isAdmin ? 'Review tickets' : 'Report Issue'}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: '#cfe8ff' }} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>

        {isAdmin ? (
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {[
                { id: 'open', label: 'Open' },
                { id: 'recommendation', label: 'Recommendations' },
                { id: 'resolved', label: 'Resolved' },
                { id: 'all', label: 'All' }
              ].map((f) => (
                <Chip
                  key={f.id}
                  label={f.label}
                  onClick={() => setAdminFilter(f.id)}
                  color={adminFilter === f.id ? 'primary' : 'default'}
                  variant={adminFilter === f.id ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 700 }}
                />
              ))}
              <Button size="small" onClick={loadAdmin} sx={{ textTransform: 'none' }}>
                Refresh
              </Button>
            </Box>
            {adminFilter === 'recommendation' ? (
              <Typography variant="caption" sx={{ display: 'block', mb: 1.25, color: 'rgba(200,226,255,0.7)' }}>
                Ideas stay here after you click Archive Idea (including resolved). Open is for bugs only.
              </Typography>
            ) : null}
            {adminLoading ? <Typography variant="body2">Loading…</Typography> : null}
            {adminItems.length === 0 && !adminLoading ? (
              <Typography variant="body2" sx={{ color: 'rgba(200,226,255,0.75)' }}>
                No tickets in this view.
              </Typography>
            ) : null}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {adminItems.map((t) => (
                <Paper
                  key={t.id}
                  elevation={0}
                  sx={{ p: 1.5, bgcolor: 'rgba(0,0,0,0.28)', border: '1px solid rgba(120,200,255,0.22)' }}
                >
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 0.75 }}>
                    <Chip size="small" className="notranslate" label={t.publicId} sx={{ fontWeight: 800 }} />
                    <Chip size="small" label={t.status} />
                    <Chip size="small" variant="outlined" label={issueLabel(t.issueType)} />
                    <Typography variant="caption" className="notranslate" translate="no" sx={{ color: 'rgba(200,226,255,0.8)' }}>
                      {emailPrefix(t.requesterEmail)}
                      {t.levelNumber != null ? ` · L${t.levelNumber}` : ''}
                      {t.exerciseNumber != null ? ` · ex ${t.exerciseNumber}` : ''}
                      {t.meTooCount > 0 ? ` · +${t.meTooCount} me-too` : ''}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    className="notranslate"
                    translate="no"
                    sx={{ display: 'block', color: 'rgba(180,210,240,0.85)', mb: 0.35 }}
                  >
                    Student: {t.requesterEmail || emailPrefix(t.requesterEmail) || '—'}
                    {Array.isArray(t.notifiedTeacherEmails) && t.notifiedTeacherEmails.length
                      ? ` · Teacher: ${t.notifiedTeacherEmails.join(', ')}`
                      : t.status === 'resolved'
                        ? ' · Teacher: (none notified — student not in a class)'
                        : ''}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', color: 'rgba(160,190,220,0.75)', mb: 0.75 }}
                  >
                    Submitted: {formatTicketWhen(t.createdAt) || '—'}
                    {t.resolvedAt ? ` · Completed: ${formatTicketWhen(t.resolvedAt)}` : ''}
                    {t.teachersNotifiedAt ? ` · Teacher notified: ${formatTicketWhen(t.teachersNotifiedAt)}` : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                    {t.body}
                  </Typography>
                  {t.hasScreenshot ? (
                    <Box
                      component="img"
                      alt="screenshot"
                      src={`/api/support/tickets/${t.id}/screenshot`}
                      sx={{ maxWidth: '100%', maxHeight: 180, borderRadius: 1, mb: 1, display: 'block' }}
                    />
                  ) : null}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {t.status !== 'in_progress' && t.status !== 'resolved' ? (
                      <Button size="small" variant="outlined" onClick={() => setStatus(t.id, 'in_progress')} sx={{ textTransform: 'none' }}>
                        In progress
                      </Button>
                    ) : null}
                    {t.status !== 'resolved' ? (
                      t.issueType === 'recommendation' ? (
                        <Button
                          size="small"
                          variant="contained"
                          color="secondary"
                          onClick={() => setStatus(t.id, 'resolved')}
                          sx={{ textTransform: 'none' }}
                          title="Removes from Open. Still listed under Recommendations and Resolved."
                        >
                          Archive Idea
                        </Button>
                      ) : (
                        <Button size="small" variant="contained" onClick={() => setStatus(t.id, 'resolved')} sx={{ textTransform: 'none' }}>
                          Mark complete
                        </Button>
                      )
                    ) : (
                      <Button size="small" onClick={() => setStatus(t.id, 'new')} sx={{ textTransform: 'none' }}>
                        Reopen
                      </Button>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: 'rgba(200,226,255,0.8)', mb: 2 }}>
              Check similar open tickets first (description preview shown). If yours is already listed, tap{' '}
              <strong>This is my issue too</strong>. Only submit a new report if nothing matches.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 1.5 }}>
              <FormControl size="small" fullWidth sx={fieldSx}>
                <InputLabel>Level</InputLabel>
                <Select label="Level" value={levelNumber} onChange={(e) => setLevelNumber(e.target.value)}>
                  {levelOptions.map((o) => (
                    <MenuItem key={o.value || 'any'} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Exercise / step #"
                type="number"
                value={exerciseNumber}
                onChange={(e) => setExerciseNumber(e.target.value)}
                inputProps={{ min: 1, max: 40 }}
                sx={fieldSx}
              />
              <FormControl size="small" fullWidth sx={{ ...fieldSx, gridColumn: { sm: 'span 2' } }}>
                <InputLabel>Issue type</InputLabel>
                <Select label="Issue type" value={issueType} onChange={(e) => setIssueType(e.target.value)}>
                  {ISSUE_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {loadingSimilar ? <Typography variant="caption">Looking for similar tickets…</Typography> : null}
            {similar.length > 0 ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                  Similar open tickets
                </Typography>
                {similar.map((t) => (
                  <Paper
                    key={t.id}
                    elevation={0}
                    sx={{ p: 1.25, mb: 1, bgcolor: 'rgba(0,0,0,0.28)', border: '1px solid rgba(120,200,255,0.2)' }}
                  >
                    <Typography variant="caption" className="notranslate" translate="no" sx={{ color: 'rgba(180,210,240,0.85)' }}>
                      {t.publicId}
                      {t.levelNumber != null ? ` · Level ${t.levelNumber}` : ''}
                      {t.exerciseNumber != null ? ` · Ex ${t.exerciseNumber}` : ''}
                      {t.meTooCount ? ` · ${t.meTooCount} others` : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', my: 0.5 }}>
                      {t.body
                        ? t.body
                        : `${issueLabel(t.issueType)}${
                            t.levelNumber != null ? ` · Level ${t.levelNumber}` : ''
                          } — no description available.`}
                    </Typography>
                    <Button size="small" variant="outlined" onClick={() => meToo(t.id)} sx={{ textTransform: 'none' }}>
                      This is my issue too
                    </Button>
                  </Paper>
                ))}
              </Box>
            ) : null}

            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
              Describe a new issue
            </Typography>
            <TextField
              size="small"
              label="Describe the issue"
              multiline
              minRows={3}
              fullWidth
              value={body}
              onChange={(e) => setBody(e.target.value)}
              inputProps={{ autoComplete: 'off' }}
              sx={{ ...fieldSx, mb: 1.5 }}
            />
            <Button component="label" variant="outlined" sx={{ textTransform: 'none', mb: 1 }}>
              {screenshotDataUrl ? `Screenshot: ${screenshotName || 'attached'}` : 'Attach screenshot (optional)'}
              <input hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={onPickFile} />
            </Button>
            {screenshotDataUrl ? (
              <Box component="img" src={screenshotDataUrl} alt="" sx={{ display: 'block', maxWidth: '100%', maxHeight: 140, borderRadius: 1, mb: 1 }} />
            ) : null}
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button variant="contained" disabled={submitting} onClick={submitNew} sx={{ textTransform: 'none', fontWeight: 800 }}>
                {submitting ? 'Submitting…' : 'Report Issue'}
              </Button>
              <Button onClick={onClose} sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
            </Box>
          </>
        )}

        {error ? (
          <Typography variant="body2" sx={{ color: '#ffb4a8', mt: 1.5 }}>
            {error}
          </Typography>
        ) : null}
        {message ? (
          <Typography variant="body2" sx={{ color: '#9ae6b4', mt: 1.5 }}>
            {message}
          </Typography>
        ) : null}
      </Paper>
    </Box>
  );
}
